import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { AUDIT_ACTIONS, LAUNCH_PLAN_CATALOG, MAX_SOLO_BRANDED_SITES, type AuditAction, type WorkspaceCapabilityDecision } from "@mph/launch-control";

import {
  getWorkspaceOverview,
  updateWorkspaceSettings,
  deleteWorkspaceProject,
  getProjectAiSettings,
  getProjectCustomHttpSettings,
  listProviderConnections,
  createProviderConnection,
  rotateProviderConnection,
  previewProviderConnectionDisconnect,
  disconnectProviderConnection,
  createWorkspaceInvitation,
  listWorkspaceInvitations,
  listWorkspaces,
  openBillingPortal,
  recordPlanIntent,
  pauseProjectAiSettings,
  revokeWorkspaceInvitation,
  retryWorkspaceInvitation,
  removeWorkspaceMember,
  updateWorkspaceMemberRole,
  connectProjectDomain,
  inspectProjectDomain,
  listProjectDomains,
  removeProjectDomain,
  retryProjectDomain,
  browseProjectData,
  restoreProjectData,
  saveProjectAiSettings,
  saveProjectCustomHttpManifest,
  saveProjectCustomHttpCredential,
  activateProjectCustomHttp,
  disableProjectCustomHttpConnection,
  getProjectApiDiscovery,
  updateProjectApiDiscovery,
  confirmProjectApiDiscovery,
  activateProjectApiDiscovery,
  saveProjectApiDiscoveryCredential,
  getProjectRevisionHistory,
  publishProjectSourceCandidate,
  selectProjectSourceCandidate,
  listConnectedDevices,
  revokeConnectedDevice,
  getProjectPublicApiSettings,
  updateProjectPublicApiSettings,
  getTeamSkillsLibrary,
  getTeamSkillHistory,
  getTeamSkillVersionComparison,
  publishTeamSkillVersion,
  changeTeamSkillState,
  beginTeamSkillOwnerStepUp,
  completeTeamSkillOwnerAction,
  replaceTeamSkillTags,
  uploadTeamSkill,
  type BillingCadence,
  type PlanIntentResult,
  type WorkspaceOverview,
  type WorkspaceSummary,
} from "../../lib/account-client";
import {
  completeAgentPublishingReview,
  cancelAgentPublishingReview,
  getAgentPublishingReview,
  type AgentPublishingReview,
} from "../../lib/agent-client";
import { posthog } from "../../lib/posthog";
import { AppLink, navigateTo, workspacePath } from "../../lib/navigation";
import { DoorwayBrandIcon } from "../public/DoorwayBrandIcon";
import { ProjectAiSettings } from "./ProjectAiSettings";
import { ProviderConnections } from "./ProviderConnections";
import { TeamInvitations } from "./TeamInvitations";
import { ProjectDomainSettings } from "./ProjectDomainSettings";
import { ProjectDataBrowser } from "./ProjectDataBrowser";
import { ProjectApiBuilder, type ProjectApiBuilderApi } from "./ProjectApiBuilder";
import { ApiDiscoveryReview, type ApiDiscoveryReviewApi } from "../upload/ApiDiscoveryReview";
import { ProjectRevisionHistory, type ProjectRevisionHistoryApi } from "./ProjectRevisionHistory";
import { ConnectedDevices } from "./ConnectedDevices";
import { ProjectAccessSettings, type ProjectAccessSettingsApi } from "./ProjectAccessSettings";
import { ProjectApiSettings } from "./ProjectApiSettings";
import { TeamSkillsLibrary, type TeamSkillsLibraryApi } from "./TeamSkillsLibrary";
import { WorkspaceNavIcon, type WorkspaceNavIconName } from "./WorkspaceNavIcon";

type WorkspaceView = "sites" | "skills" | "ai" | "domains" | "people" | "billing" | "history" | "settings";

const WORKSPACE_VIEWS: Array<{ id: WorkspaceView; label: string; icon: WorkspaceNavIconName }> = [
  { id: "sites", label: "Sites", icon: "sites" },
  { id: "skills", label: "Skills", icon: "skills" },
  { id: "ai", label: "AI connections", icon: "sparkle" },
  { id: "domains", label: "Domains", icon: "globe" },
  { id: "people", label: "People", icon: "people" },
  { id: "billing", label: "Billing", icon: "billing" },
  { id: "history", label: "History", icon: "history" },
  { id: "settings", label: "Settings", icon: "settings" },
];

export interface WorkspacePageApi {
  listWorkspaces: typeof listWorkspaces;
  getWorkspaceOverview: typeof getWorkspaceOverview;
  updateWorkspaceSettings: typeof updateWorkspaceSettings;
  deleteWorkspaceProject: typeof deleteWorkspaceProject;
  recordPlanIntent: typeof recordPlanIntent;
  openBillingPortal: typeof openBillingPortal;
  getProjectAiSettings: typeof getProjectAiSettings;
  saveProjectAiSettings: typeof saveProjectAiSettings;
  pauseProjectAiSettings: typeof pauseProjectAiSettings;
  getProjectCustomHttpSettings?: typeof getProjectCustomHttpSettings;
  saveProjectCustomHttpManifest?: typeof saveProjectCustomHttpManifest;
  saveProjectCustomHttpCredential?: typeof saveProjectCustomHttpCredential;
  activateProjectCustomHttp?: typeof activateProjectCustomHttp;
  disableProjectCustomHttpConnection?: typeof disableProjectCustomHttpConnection;
  getProjectApiDiscovery?: typeof getProjectApiDiscovery;
  updateProjectApiDiscovery?: typeof updateProjectApiDiscovery;
  confirmProjectApiDiscovery?: typeof confirmProjectApiDiscovery;
  activateProjectApiDiscovery?: typeof activateProjectApiDiscovery;
  saveProjectApiDiscoveryCredential?: typeof saveProjectApiDiscoveryCredential;
  getAgentPublishingReview?: typeof getAgentPublishingReview;
  completeAgentPublishingReview?: typeof completeAgentPublishingReview;
  cancelAgentPublishingReview?: typeof cancelAgentPublishingReview;
  listProviderConnections: typeof listProviderConnections;
  createProviderConnection: typeof createProviderConnection;
  rotateProviderConnection: typeof rotateProviderConnection;
  previewProviderConnectionDisconnect: typeof previewProviderConnectionDisconnect;
  disconnectProviderConnection: typeof disconnectProviderConnection;
  listWorkspaceInvitations: typeof listWorkspaceInvitations;
  createWorkspaceInvitation: typeof createWorkspaceInvitation;
  revokeWorkspaceInvitation: typeof revokeWorkspaceInvitation;
  retryWorkspaceInvitation: typeof retryWorkspaceInvitation;
  updateWorkspaceMemberRole: typeof updateWorkspaceMemberRole;
  removeWorkspaceMember: typeof removeWorkspaceMember;
  listProjectDomains: typeof listProjectDomains;
  connectProjectDomain: typeof connectProjectDomain;
  inspectProjectDomain: typeof inspectProjectDomain;
  removeProjectDomain: typeof removeProjectDomain;
  retryProjectDomain: typeof retryProjectDomain;
  browseProjectData: typeof browseProjectData;
  restoreProjectData: typeof restoreProjectData;
  getProjectRevisionHistory?: typeof getProjectRevisionHistory;
  selectProjectSourceCandidate?: typeof selectProjectSourceCandidate;
  publishProjectSourceCandidate?: typeof publishProjectSourceCandidate;
  listConnectedDevices?: typeof listConnectedDevices;
  revokeConnectedDevice?: typeof revokeConnectedDevice;
  getProjectPublicApiSettings?: typeof getProjectPublicApiSettings;
  updateProjectPublicApiSettings?: typeof updateProjectPublicApiSettings;
  getTeamSkillsLibrary?: typeof getTeamSkillsLibrary;
  getTeamSkillHistory?: typeof getTeamSkillHistory;
  getTeamSkillVersionComparison?: typeof getTeamSkillVersionComparison;
  publishTeamSkillVersion?: typeof publishTeamSkillVersion;
  changeTeamSkillState?: typeof changeTeamSkillState;
  beginTeamSkillOwnerStepUp?: typeof beginTeamSkillOwnerStepUp;
  completeTeamSkillOwnerAction?: typeof completeTeamSkillOwnerAction;
  replaceTeamSkillTags?: typeof replaceTeamSkillTags;
  uploadTeamSkill?: typeof uploadTeamSkill;
}

const defaultApi: WorkspacePageApi = {
  listWorkspaces,
  getWorkspaceOverview,
  updateWorkspaceSettings,
  deleteWorkspaceProject,
  recordPlanIntent,
  openBillingPortal,
  getProjectAiSettings,
  saveProjectAiSettings,
  pauseProjectAiSettings,
  getProjectCustomHttpSettings,
  saveProjectCustomHttpManifest,
  saveProjectCustomHttpCredential,
  activateProjectCustomHttp,
  disableProjectCustomHttpConnection,
  getProjectApiDiscovery,
  updateProjectApiDiscovery,
  confirmProjectApiDiscovery,
  activateProjectApiDiscovery,
  saveProjectApiDiscoveryCredential,
  getAgentPublishingReview,
  completeAgentPublishingReview,
  cancelAgentPublishingReview,
  listProviderConnections,
  createProviderConnection,
  rotateProviderConnection,
  previewProviderConnectionDisconnect,
  disconnectProviderConnection,
  listWorkspaceInvitations,
  createWorkspaceInvitation,
  revokeWorkspaceInvitation,
  retryWorkspaceInvitation,
  updateWorkspaceMemberRole,
  removeWorkspaceMember,
  listProjectDomains,
  connectProjectDomain,
  inspectProjectDomain,
  removeProjectDomain,
  retryProjectDomain,
  browseProjectData,
  restoreProjectData,
  getProjectRevisionHistory,
  selectProjectSourceCandidate,
  publishProjectSourceCandidate,
  listConnectedDevices,
  revokeConnectedDevice,
  getProjectPublicApiSettings,
  updateProjectPublicApiSettings,
  getTeamSkillsLibrary,
  getTeamSkillHistory,
  getTeamSkillVersionComparison,
  publishTeamSkillVersion,
  changeTeamSkillState,
  beginTeamSkillOwnerStepUp,
  completeTeamSkillOwnerAction,
  replaceTeamSkillTags,
  uploadTeamSkill,
};

async function loadDefaultWorkspace(api: WorkspacePageApi): Promise<[WorkspaceSummary[], WorkspaceOverview]> {
  const workspaces = await api.listWorkspaces();
  const workspaceId = workspaces[0]?.id;
  if (!workspaceId) throw new Error("This account does not have a workspace yet.");
  return [workspaces, await api.getWorkspaceOverview(workspaceId)];
}

export function WorkspacePage({
  workspaceId,
  initialWorkspaces,
  api = defaultApi,
}: {
  workspaceId: string | null;
  initialWorkspaces?: WorkspaceSummary[];
  api?: WorkspacePageApi;
}) {
  const [workspaces, setWorkspaces] = useState<WorkspaceSummary[]>(initialWorkspaces ?? []);
  const [overview, setOverview] = useState<WorkspaceOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [pendingPlan, setPendingPlan] = useState<"solo" | "team" | null>(null);
  const [openingPortal, setOpeningPortal] = useState(false);
  const [planCadence, setPlanCadence] = useState<BillingCadence>(initialBillingCadence);
  const [soloBrandedSites, setSoloBrandedSites] = useState(1);
  const [activeView, setActiveView] = useState<WorkspaceView>(initialWorkspaceView);
  const [siteSearch, setSiteSearch] = useState("");
  const [domainSearch, setDomainSearch] = useState("");
  const [expandedProjectId, setExpandedProjectId] = useState<string | null>(null);
  const [expandedDomainProjectId, setExpandedDomainProjectId] = useState<string | null>(null);
  const [agentReviewOperationId] = useState(() => typeof window === "undefined"
    ? null
    : new URL(window.location.href).searchParams.get("agentReview"));
  const [agentReview, setAgentReview] = useState<AgentPublishingReview | null>(null);
  const [agentReviewError, setAgentReviewError] = useState<string | null>(null);
  const [agentReviewPending, setAgentReviewPending] = useState(false);

  const changeDomainUsage = (difference: number) => {
    setOverview((current) => current ? {
      ...current,
      domainUsage: {
        ...current.domainUsage,
        used: Math.max(0, Math.min(current.domainUsage.limit, current.domainUsage.used + difference)),
      },
    } : current);
  };

  const changeConnectedDomain = useCallback((projectId: string, customDomain: string | null) => {
    setOverview((current) => current ? {
      ...current,
      projects: current.projects.map((project) => project.id === projectId
        ? withCustomDomain(project, customDomain)
        : project),
    } : current);
  }, []);

  const removeProject = (projectId: string, removedDomains: number) => {
    setOverview((current) => current ? {
      ...current,
      projects: current.projects.filter((project) => project.id !== projectId),
      domainUsage: {
        ...current.domainUsage,
        used: Math.max(0, current.domainUsage.used - removedDomains),
      },
    } : current);
    setExpandedProjectId(null);
    setExpandedDomainProjectId(null);
  };

  const replaceWorkspace = (workspace: WorkspaceOverview["workspace"]) => {
    setOverview((current) => current ? { ...current, workspace } : current);
    setWorkspaces((current) => current.map((item) => item.id === workspace.id
      ? { id: workspace.id, name: workspace.name, role: workspace.role, capabilities: item.capabilities, ...(workspace.iconUrl ? { iconUrl: workspace.iconUrl } : {}) }
      : item));
  };

  useEffect(() => {
    let active = true;
    setError(null);
    const loadWorkspace = async () => {
      let items: WorkspaceSummary[];
      let result: WorkspaceOverview;
      if (initialWorkspaces !== undefined) {
        const selectedWorkspaceId = workspaceId ?? initialWorkspaces[0]?.id;
        if (!selectedWorkspaceId) throw new Error("This account does not have a workspace yet.");
        items = initialWorkspaces;
        result = await api.getWorkspaceOverview(selectedWorkspaceId);
      } else {
        [items, result] = workspaceId
          ? await Promise.all([api.listWorkspaces(), api.getWorkspaceOverview(workspaceId)])
          : await loadDefaultWorkspace(api);
      }
      if (!active) return;
      setWorkspaces(items);
      setOverview(result);
    };
    void loadWorkspace().catch((cause: unknown) => {
      if (active) setError(cause instanceof Error ? cause.message : "Doorway could not load this workspace.");
    });
    return () => { active = false; };
  }, [api, initialWorkspaces, workspaceId]);

  useEffect(() => {
    if (!agentReviewOperationId || !overview || !api.getAgentPublishingReview) return;
    let active = true;
    setAgentReviewError(null);
    void api.getAgentPublishingReview(agentReviewOperationId).then((review) => {
      if (!active) return;
      if (review.workspaceId !== overview.workspace.id
        || !overview.projects.some((project) => project.id === review.projectId)) {
        throw new Error("This agent review does not belong to the open workspace.");
      }
      setAgentReview(review);
      setActiveView("sites");
      setSiteSearch("");
      setExpandedProjectId(review.projectId);
    }).catch((cause: unknown) => {
      if (active) setAgentReviewError(cause instanceof Error ? cause.message : "Doorway could not open this agent review.");
    });
    return () => { active = false; };
  }, [agentReviewOperationId, api.getAgentPublishingReview, overview]);

  const finishAgentReview = async () => {
    if (!agentReview || agentReview.status !== "pending" || agentReviewPending
      || !api.completeAgentPublishingReview) return;
    setAgentReviewPending(true);
    setAgentReviewError(null);
    try {
      const completed = await api.completeAgentPublishingReview(
        agentReview.operationId,
        agentReview.revisionId,
      );
      setAgentReview(completed);
      setNotice("The exact agent revision is active.");
      const url = new URL(window.location.href);
      url.searchParams.delete("agentReview");
      window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
    } catch (cause) {
      setAgentReviewError(cause instanceof Error ? cause.message : "Doorway could not finish this agent publication.");
    } finally {
      setAgentReviewPending(false);
    }
  };

  const cancelAgentReview = async () => {
    if (!agentReview || agentReview.status !== "pending" || agentReviewPending
      || !api.cancelAgentPublishingReview) return;
    setAgentReviewPending(true);
    setAgentReviewError(null);
    try {
      const canceled = await api.cancelAgentPublishingReview(agentReview.operationId);
      setAgentReview(canceled);
      setNotice("The agent publication was canceled and its deferred revision was removed.");
      const url = new URL(window.location.href);
      url.searchParams.delete("agentReview");
      window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
    } catch (cause) {
      setAgentReviewError(cause instanceof Error ? cause.message : "Doorway could not cancel this agent publication.");
    } finally {
      setAgentReviewPending(false);
    }
  };

  const activatedAgentReview = () => {
    setAgentReview((current) => current && current.status === "pending"
      ? { ...current, reviewState: "ready_to_finalize" }
      : current);
    void finishAgentReview();
  };

  const preparePlan = async (
    plan: "solo" | "team",
    cadence: BillingCadence = "monthly",
    billingQuantity = 1,
  ) => {
    if (!overview || pendingPlan) return;
    setPendingPlan(plan);
    setNotice(null);
    posthog.capture("plan_upgrade_started", { plan, cadence, billing_quantity: billingQuantity });
    try {
      const result = await api.recordPlanIntent(overview.workspace.id, plan, cadence, billingQuantity);
      setOverview((current) => current
        ? { ...current, plan: { ...current.plan, desired: result.desiredPlan } }
        : current);
      setNotice(planNotice(result));
      if (result.billingReady && result.checkoutUrl) window.location.assign(result.checkoutUrl);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Doorway could not prepare this plan.");
    } finally {
      setPendingPlan(null);
    }
  };

  const manageBilling = async () => {
    if (!overview || openingPortal) return;
    setOpeningPortal(true);
    setError(null);
    try {
      const result = await api.openBillingPortal(overview.workspace.id);
      posthog.capture("billing_portal_opened");
      window.location.assign(result.portalUrl);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Doorway could not open billing management.");
    } finally {
      setOpeningPortal(false);
    }
  };

  const changeMemberRole = async (userId: string, role: "creator" | "viewer") => {
    if (!overview) return;
    setError(null);
    try {
      await api.updateWorkspaceMemberRole(overview.workspace.id, userId, role);
      setOverview((current) => current ? {
        ...current,
        members: current.members.map((member) => member.id === userId ? { ...member, role } : member),
      } : current);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Doorway could not update this member.");
    }
  };

  const removeMember = async (userId: string) => {
    if (!overview) return;
    setError(null);
    try {
      await api.removeWorkspaceMember(overview.workspace.id, userId);
      setOverview((current) => current ? {
        ...current,
        members: current.members.filter((member) => member.id !== userId),
      } : current);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Doorway could not remove this member.");
    }
  };

  const selectView = (view: WorkspaceView) => {
    setActiveView(view);
    if (typeof window !== "undefined") {
      const url = new URL(window.location.href);
      url.searchParams.delete("plan");
      url.hash = view;
      window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
    }
  };

  const visibleSiteProjects = overview ? filterProjects(overview.projects, siteSearch) : [];
  const visibleDomainProjects = overview ? filterProjects(overview.projects, domainSearch) : [];
  const projectApiBuilderApi = projectApiBuilderApiFor(api);
  const apiDiscoveryReviewApi = apiDiscoveryReviewApiFor(api);
  const teamSkillsApi = useMemo(() => teamSkillsApiFor(api), [api]);
  const publicApi = publicApiFor(api);
  const sourceSyncApi = sourceSyncApiFor(api);

  return (
    <div className="app-shell workspace-shell workspace-oasis-shell">
      <header className="site-header workspace-header">
        <AppLink className="wordmark" href="/manage" aria-label="Doorway home">
          <DoorwayBrandIcon className="workspace-brand-icon" name="logo-tile-green" />
          Doorway
        </AppLink>
        <div className="workspace-header-status" aria-live="polite">
          <span aria-hidden="true" />
          {overview?.workspace.name ?? "Opening your workspace"}
        </div>
        <AppLink className="primary-button workspace-header-publish" href="/manage">Publish an app</AppLink>
      </header>

      <main className="workspace-main">
        {error && <p className="error-message workspace-error" role="alert">{error}</p>}
        {!overview && !error && (
          <section className="workspace-loading" aria-label="Loading workspace">
            <span className="progress-orbit" aria-hidden="true" />
            <p>Opening your workspace</p>
          </section>
        )}
        {overview && (
          <>
            <section className={`workspace-hero${workspaces.length > 1 ? " workspace-hero-switchable" : ""}`} aria-labelledby="workspace-heading">
              <div>
                <p className="eyebrow">Workspace</p>
                <h1 id="workspace-heading">{overview.workspace.name}</h1>
                <p>Everything you publish, share, and manage lives here.</p>
              </div>
              {workspaces.length > 1 && (
                <label className="workspace-picker">
                  <span>Switch workspace</span>
                  <select
                    value={overview.workspace.id}
                    onChange={(event) => navigateTo(workspacePath(event.target.value))}
                  >
                    {workspaces.map((workspace) => (
                      <option key={workspace.id} value={workspace.id}>{workspace.name}</option>
                    ))}
                  </select>
                </label>
              )}
            </section>

            {notice && <p className="workspace-notice" role="status">{notice}</p>}
            {agentReviewError && !agentReview ? <p className="error-message" role="alert">{agentReviewError}</p> : null}

            <div className="workspace-dashboard">
              <nav className="workspace-nav" aria-label="Workspace sections">
                <div className="workspace-nav-heading">
                  {overview.workspace.iconUrl
                    ? <img className="workspace-nav-icon" src={overview.workspace.iconUrl} alt="" />
                    : <span className="workspace-nav-door" aria-hidden="true"><span /></span>}
                  <span><strong>{overview.workspace.name}</strong><small>{planTitle(overview.plan.effective)} workspace</small></span>
                </div>
                <p className="workspace-nav-label">Manage</p>
                <div className="workspace-nav-links">
                  {WORKSPACE_VIEWS.filter((view) => view.id !== "skills" || overview.capabilities.teamSkills.state !== "upgrade_required").map((view) => (
                    <button
                      key={view.id}
                      type="button"
                      aria-label={view.label}
                      aria-current={activeView === view.id ? "page" : undefined}
                      onClick={() => selectView(view.id)}
                    >
                      <WorkspaceNavIcon name={view.icon} />
                      <span>{view.label}</span>
                      <small>{workspaceViewBadge(view.id, overview)}</small>
                    </button>
                  ))}
                </div>
                <div className="workspace-sidebar-note">
                  <DoorwayBrandIcon name="door-solid-green" />
                  <span><strong>Local-first publishing</strong><small>Your files stay yours until review.</small></span>
                </div>
              </nav>

              <div className="workspace-view">
                {activeView === "sites" && (
                  <section className="workspace-section workspace-sites-section" aria-label="Sites">
                    <div className="workspace-section-heading">
                      <div><p className="step-label">Sites</p><h2>Your sites</h2></div>
                      <AppLink className="primary-button workspace-publish-button" href="/manage">Publish another</AppLink>
                    </div>
                    <p className="workspace-section-copy">Find a site, then open its row to manage access, AI, domains, and saved data.</p>
                    <label className="workspace-search">
                      <span>Search sites</span>
                      <input type="search" aria-label="Search sites" placeholder="Search by site, owner, or access" value={siteSearch} onChange={(event) => setSiteSearch(event.target.value)} />
                    </label>
                    <div className="workspace-site-table">
                      <div className="workspace-site-table-head" aria-hidden="true"><span>Site</span><span>Access</span><span>Updated</span><span /></div>
                      {overview.projects.length === 0 ? (
                        <p className="workspace-empty">Publish your first app and it will appear here.</p>
                      ) : visibleSiteProjects.length === 0 ? (
                        <p className="workspace-empty">No sites match that search.</p>
                      ) : visibleSiteProjects.map((project) => {
                        const expanded = expandedProjectId === project.id;
                        const panelId = `site-settings-${project.id}`;
                        const title = projectTitle(project);
                        const projectCapabilities = project.capabilities ?? overview.capabilities;
                        return (
                          <article className={`workspace-site-row${expanded ? " workspace-site-row-open" : ""}`} key={project.id}>
                            <button
                              className="workspace-site-summary"
                              type="button"
                              aria-expanded={expanded}
                              aria-controls={panelId}
                              aria-label={`${expanded ? "Close" : "Open"} ${title} settings`}
                              onClick={() => setExpandedProjectId(expanded ? null : project.id)}
                            >
                              <span className="workspace-site-identity"><span className={`project-status project-${project.status}`}>{project.status}</span><span><strong>{title}</strong><small>{project.customDomain ? `Doorway name: ${project.name}` : project.description ?? `Owned by ${project.ownerName}`}</small></span></span>
                              <span className="workspace-site-cell"><small>Access</small><strong>{visibilityLabel(project.visibility)}</strong></span>
                              <span className="workspace-site-cell"><small>Updated</small><time dateTime={project.updatedAt}>{formatDate(project.updatedAt)}</time></span>
                              <span className="workspace-row-chevron" aria-hidden="true" />
                            </button>
                            {expanded && (
                              <div className="workspace-site-panel" id={panelId} role="region" aria-label={`${title} settings`}>
                                <div className="workspace-site-facts">
                                  <div><span>Owner</span><strong>{project.ownerName}</strong></div>
                                  <div><span>Status</span><strong>{project.status}</strong></div>
                                  <div><span>Access</span><strong>{visibilityLabel(project.visibility)}</strong></div>
                                  <div><span>Last updated</span><strong>{formatDate(project.updatedAt)}</strong></div>
                                </div>
                                <div className="workspace-site-tools">
                                  {agentReview?.projectId === project.id ? (
                                    <section className="workspace-tool-card" aria-label="Finish agent publication">
                                      <p className="step-label">Agent publishing</p>
                                      <h3>Finish the exact revision</h3>
                                      <p>Review revision <code>{agentReview.revisionId}</code>. Doorway will activate this stored snapshot after its connections are approved.</p>
                                      {agentReview.status === "completed" ? (
                                        <p className="workspace-notice" role="status">This exact agent revision is active.</p>
                                      ) : agentReview.status !== "pending" ? (
                                        <p className="workspace-notice" role="status">This agent publication is {agentReview.status}.</p>
                                      ) : agentReview.reviewState === "ready_to_finalize" ? (
                                        <button className="primary-button" type="button" disabled={agentReviewPending} onClick={() => void finishAgentReview()}>
                                          {agentReviewPending ? "Finishing..." : "Finish agent publication"}
                                        </button>
                                      ) : agentReview.reviewKind === "discovery" && apiDiscoveryReviewApi ? (
                                        <ApiDiscoveryReview
                                          workspaceId={overview.workspace.id}
                                          projectId={project.id}
                                          expectedRevisionId={agentReview.revisionId}
                                          expectedAgentOperationId={agentReview.operationId}
                                          api={apiDiscoveryReviewApi}
                                          onActivated={activatedAgentReview}
                                        />
                                      ) : agentReview.reviewKind === "manifest" && projectApiBuilderApi ? (
                                        <ProjectApiBuilder
                                          workspaceId={overview.workspace.id}
                                          projectId={project.id}
                                          expectedRevisionId={agentReview.revisionId}
                                          expectedAgentOperationId={agentReview.operationId}
                                          api={projectApiBuilderApi}
                                          onActivated={activatedAgentReview}
                                        />
                                      ) : <p className="workspace-tool-lock">The exact review controls are temporarily unavailable.</p>}
                                      {agentReview.status === "pending" && api.cancelAgentPublishingReview ? (
                                        <button className="secondary-button" type="button" disabled={agentReviewPending} onClick={() => void cancelAgentReview()}>
                                          {agentReviewPending ? "Working..." : "Cancel agent publication"}
                                        </button>
                                      ) : null}
                                      {agentReviewError ? <p className="error-message" role="alert">{agentReviewError}</p> : null}
                                    </section>
                                  ) : null}
                                  <section className="workspace-tool-card"><p className="step-label">AI</p><h3>Model settings</h3><p>Choose one connection, model, and monthly run cap for this project.</p><CapabilityBoundary decision={projectCapabilities.managedAi} label="Managed AI"><ProjectAiSettings workspaceId={overview.workspace.id} projectId={project.id} api={api} /></CapabilityBoundary></section>
                                  <section className="workspace-tool-card"><p className="step-label">Domain</p><h3>Custom address</h3>{overview.limits.customDomains > 0 && overview.workspace.role === "owner" ? <ProjectDomainSettings workspaceId={overview.workspace.id} projectId={project.id} api={api} onUsageChange={changeDomainUsage} onConnectedDomainChange={changeConnectedDomain} /> : <p className="workspace-tool-lock">Custom domains start with Solo. Open Billing to upgrade.</p>}</section>
                                  <section className="workspace-tool-card"><p className="step-label">Data</p><h3>Collections and backups</h3><CapabilityBoundary decision={projectCapabilities.hostedCollections} label="Hosted collections"><ProjectDataBrowser workspaceId={overview.workspace.id} projectId={project.id} api={api} /></CapabilityBoundary></section>
                                  {agentReview?.projectId !== project.id ? <section className="workspace-tool-card"><CapabilityBoundary decision={projectCapabilities.customApis} label="Custom APIs" transportAvailable={Boolean(projectApiBuilderApi)}>{projectApiBuilderApi ? <ProjectApiBuilder workspaceId={overview.workspace.id} projectId={project.id} api={projectApiBuilderApi} /> : null}</CapabilityBoundary></section> : null}
                                  {projectCapabilities.publicCredentialApps.state === "enabled" && publicApi ? (
                                    <>
                                      <section className="workspace-tool-card"><ProjectAccessSettings
                                        workspaceId={overview.workspace.id}
                                        projectId={project.id}
                                        api={{
                                          getProjectPublicApiSettings: publicApi.getProjectPublicApiSettings,
                                          updateProjectPublicApiSettings: publicApi.updateProjectPublicApiSettings,
                                        }}
                                      /></section>
                                      <section className="workspace-tool-card"><ProjectApiSettings
                                        workspaceId={overview.workspace.id}
                                        projectId={project.id}
                                        api={{
                                          getProjectPublicApiSettings: publicApi.getProjectPublicApiSettings,
                                          updateProjectPublicApiSettings: publicApi.updateProjectPublicApiSettings,
                                        }}
                                      /></section>
                                    </>
                                  ) : <section className="workspace-tool-card"><CapabilityBoundary decision={projectCapabilities.publicCredentialApps} label="Public credential apps" transportAvailable={Boolean(publicApi)} /></section>}
                                  {projectCapabilities.sourceSync.state === "enabled" && sourceSyncApi ? (
                                    <section className="workspace-tool-card"><ProjectRevisionHistory
                                      workspaceId={overview.workspace.id}
                                      projectId={project.id}
                                      api={{
                                        getProjectRevisionHistory: sourceSyncApi.getProjectRevisionHistory,
                                        selectProjectSourceCandidate: sourceSyncApi.selectProjectSourceCandidate,
                                        publishProjectSourceCandidate: sourceSyncApi.publishProjectSourceCandidate,
                                      }}
                                    /></section>
                                  ) : <section className="workspace-tool-card"><CapabilityBoundary decision={projectCapabilities.sourceSync} label="Source sync" transportAvailable={Boolean(sourceSyncApi)} /></section>}
                                </div>
                                {project.canDelete && (
                                  <ProjectDeleteControl
                                    workspaceId={overview.workspace.id}
                                    project={project}
                                    api={api}
                                    onDeleted={removeProject}
                                  />
                                )}
                              </div>
                            )}
                          </article>
                        );
                      })}
                    </div>
                  </section>
                )}

                {activeView === "skills" && overview.capabilities.teamSkills.state !== "upgrade_required" && (
                  <section className="workspace-section" aria-label="Team Skills">
                    <CapabilityBoundary decision={overview.capabilities.teamSkills} label="Team Skills" transportAvailable={Boolean(teamSkillsApi)}>
                      {teamSkillsApi ? <TeamSkillsLibrary workspaceId={overview.workspace.id} role={overview.workspace.role} api={teamSkillsApi} /> : null}
                    </CapabilityBoundary>
                  </section>
                )}

                {activeView === "ai" && (
                  <section className="workspace-section" aria-label="AI connections">
                    <CapabilityBoundary decision={overview.capabilities.managedAi} label="Managed AI"><ProviderConnections workspaceId={overview.workspace.id} api={api} /></CapabilityBoundary>
                  </section>
                )}

                {activeView === "domains" && (
                  <section className="workspace-section workspace-domains-section" aria-labelledby="domains-heading">
                    <div className="workspace-section-heading"><div><p className="step-label">Domains</p><h2 id="domains-heading">Custom domains</h2></div><span className="people-count">{overview.domainUsage.used} / {overview.domainUsage.limit} used</span></div>
                    <p className="workspace-section-copy">Choose a site to connect a domain, copy its DNS records, or check SSL progress.</p>
                    {overview.limits.customDomains === 0 ? (
                      <div className="workspace-domain-lock"><div><strong>Put your own name on a Doorway site</strong><span>Solo and Team include custom domains with managed DNS checks and SSL.</span></div><button className="primary-button" type="button" onClick={() => selectView("billing")}>View upgrades</button></div>
                    ) : (
                      <>
                        <label className="workspace-search"><span>Search sites</span><input type="search" aria-label="Search domain sites" placeholder="Find the site for this domain" value={domainSearch} onChange={(event) => setDomainSearch(event.target.value)} /></label>
                        <div className="workspace-domain-sites">
                          {visibleDomainProjects.length === 0 && <p className="workspace-empty">No sites match that search.</p>}
                          {visibleDomainProjects.map((project) => {
                            const expanded = expandedDomainProjectId === project.id;
                            const panelId = `domain-settings-${project.id}`;
                            const title = projectTitle(project);
                            return (
                              <article className={`workspace-domain-site${expanded ? " workspace-domain-site-open" : ""}`} key={project.id}>
                                <button type="button" aria-expanded={expanded} aria-controls={panelId} aria-label={`Manage domains for ${title}`} onClick={() => setExpandedDomainProjectId(expanded ? null : project.id)}>
                                  <span><strong>{title}</strong><small>{project.customDomain ? `Doorway name: ${project.name}` : project.status === "live" ? "Ready for a custom domain" : `${project.status} site`}</small></span>
                                  <span className="workspace-domain-route">domains.madewithdoorway.com</span>
                                  <span className="workspace-row-chevron" aria-hidden="true" />
                                </button>
                                {expanded && <div className="workspace-domain-panel" id={panelId} role="region" aria-label={`Domains for ${title}`}><ProjectDomainSettings workspaceId={overview.workspace.id} projectId={project.id} api={api} onUsageChange={changeDomainUsage} onConnectedDomainChange={changeConnectedDomain} /></div>}
                              </article>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </section>
                )}

                {activeView === "people" && (
                  <section className="workspace-section people-panel" aria-labelledby="people-heading">
                    <div className="workspace-section-heading"><div><p className="step-label">People</p><h2 id="people-heading">Who can manage this</h2></div><span className="people-count">{overview.members.length} {overview.members.length === 1 ? "person" : "people"}</span></div>
                    <ul className="member-list">
                      {overview.members.map((member) => <li key={member.id}><span className="member-avatar" aria-hidden="true">{member.name.charAt(0)}</span><span><strong>{member.name}</strong><small>{member.email}</small></span>{overview.plan.effective === "team" && overview.workspace.role === "owner" && member.role !== "owner" ? <span className="member-controls"><select aria-label={`Role for ${member.name}`} value={member.role} onChange={(event) => void changeMemberRole(member.id, event.target.value === "viewer" ? "viewer" : "creator")}><option value="creator">Creator</option><option value="viewer">Viewer</option></select><button type="button" className="text-button" onClick={() => void removeMember(member.id)}>Remove</button></span> : <em>{member.role}</em>}</li>)}
                    </ul>
                    {overview.plan.effective === "team" && overview.workspace.role === "owner" ? <TeamInvitations workspaceId={overview.workspace.id} api={api} embedded /> : overview.workspace.role === "owner" ? <div className="invite-plan-lock"><div><strong>Invite teammates with Team</strong><span>Team includes 10 creator seats, private Google sign-in, roles, and simple offboarding.</span></div><button className="primary-button" type="button" disabled={pendingPlan !== null} onClick={() => void preparePlan("team", planCadence)}>{pendingPlan === "team" ? "Opening checkout..." : "Upgrade to Team"}</button></div> : <p className="section-note">Only the workspace owner can invite or remove people.</p>}
                  </section>
                )}

                {activeView === "billing" && (
                  <div className="workspace-billing-view">
                    <section className="workspace-section plan-panel" aria-labelledby="plan-heading">
                      <div className="workspace-section-heading"><div><p className="step-label">Current plan</p><h2 id="plan-heading">{planTitle(overview.plan.effective)} plan</h2></div><div className="current-plan-status"><span className={`access-state access-${overview.plan.accessState}`}>{overview.plan.accessState}</span><span>{billingLabel(overview)}</span></div></div>
                      <p className="workspace-section-copy">{overview.plan.desired ? `${planTitle(overview.plan.desired)} is selected. Your current plan stays the same until payment succeeds.` : currentPlanDescription(overview.plan.effective)}</p>
                      <div className="limit-strip" aria-label="Current plan details"><Limit label="Live apps" value={`${overview.projects.filter((project) => project.status === "live").length} / ${overview.limits.liveApps}`} /><Limit label="Creator seats" value={`${overview.members.filter((member) => member.role !== "viewer").length} / ${overview.limits.creatorSeats}`} /><Limit {...visitLimit(overview)} /></div>
                      {overview.plan.effective !== "free" && overview.workspace.role === "owner" && <button className="secondary-button manage-billing-button" type="button" disabled={openingPortal} onClick={() => void manageBilling()}>{openingPortal ? "Opening billing..." : "Manage billing"}</button>}
                    </section>
                    {overview.plan.effective !== "team" && (
                      <section className="workspace-section upgrade-panel" aria-labelledby="upgrade-heading">
                        <div className="upgrade-heading-row"><div><p className="step-label">Upgrade</p><h2 id="upgrade-heading">Give your apps more room.</h2><p>Get more live apps, more monthly visits, custom domains, and a workspace your team can share.</p></div><div className="billing-choice" role="group" aria-label="Upgrade billing"><button type="button" aria-pressed={planCadence === "monthly"} onClick={() => setPlanCadence("monthly")}>Monthly</button><button type="button" aria-pressed={planCadence === "annual"} onClick={() => setPlanCadence("annual")}>Annual <span>2 months free</span></button></div></div>
                        <div className={`upgrade-grid${overview.plan.effective === "solo" ? " upgrade-grid-single" : ""}`}>
                          {overview.plan.effective === "free" && <UpgradeCard plan="solo" cadence={planCadence} quantity={soloBrandedSites} onQuantityChange={setSoloBrandedSites} pending={pendingPlan === "solo"} disabled={pendingPlan !== null || overview.workspace.role !== "owner"} onUpgrade={() => void preparePlan("solo", planCadence, soloBrandedSites)} />}
                          <UpgradeCard plan="team" cadence={planCadence} pending={pendingPlan === "team"} disabled={pendingPlan !== null || overview.workspace.role !== "owner"} onUpgrade={() => void preparePlan("team", planCadence)} />
                        </div>
                        {overview.workspace.role !== "owner" && <p className="upgrade-owner-note">Only the workspace owner can change the plan.</p>}
                      </section>
                    )}
                  </div>
                )}

                {activeView === "history" && (
                  <section className="workspace-section compact-section" aria-labelledby="history-heading"><p className="step-label">History</p><h2 id="history-heading">Recent workspace changes</h2><ol className="audit-list">{overview.audit.map((event) => <li key={event.id}><span>{auditActionLabel(event.action)}</span><span>{event.actorName}</span><time dateTime={event.occurredAt}>{formatDate(event.occurredAt)}</time></li>)}</ol></section>
                )}

                {activeView === "settings" && (
                  <div className="review-stack">
                    <WorkspaceSettingsPanel workspace={overview.workspace} api={api} onUpdated={replaceWorkspace} />
                    <CapabilityBoundary
                      decision={overview.capabilities.sourceSync}
                      label="Source sync"
                      transportAvailable={Boolean(api.listConnectedDevices && api.revokeConnectedDevice)}
                    >
                      {api.listConnectedDevices && api.revokeConnectedDevice ? (
                        <ConnectedDevices workspaceId={overview.workspace.id} api={{
                          listConnectedDevices: api.listConnectedDevices,
                          revokeConnectedDevice: api.revokeConnectedDevice,
                        }} />
                      ) : null}
                    </CapabilityBoundary>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

function projectApiBuilderApiFor(api: WorkspacePageApi): ProjectApiBuilderApi | null {
  if (
    api.getProjectCustomHttpSettings
    && api.saveProjectCustomHttpManifest
    && api.saveProjectCustomHttpCredential
    && api.activateProjectCustomHttp
    && api.disableProjectCustomHttpConnection
  ) {
    return {
      getProjectCustomHttpSettings: api.getProjectCustomHttpSettings,
      saveProjectCustomHttpManifest: api.saveProjectCustomHttpManifest,
      saveProjectCustomHttpCredential: api.saveProjectCustomHttpCredential,
      activateProjectCustomHttp: api.activateProjectCustomHttp,
      disableProjectCustomHttpConnection: api.disableProjectCustomHttpConnection,
    };
  }
  return null;
}

function apiDiscoveryReviewApiFor(api: WorkspacePageApi): ApiDiscoveryReviewApi | null {
  if (!api.getProjectApiDiscovery || !api.updateProjectApiDiscovery
    || !api.confirmProjectApiDiscovery || !api.activateProjectApiDiscovery
    || !api.saveProjectApiDiscoveryCredential) return null;
  return {
    getProjectApiDiscovery: api.getProjectApiDiscovery,
    updateProjectApiDiscovery: api.updateProjectApiDiscovery,
    confirmProjectApiDiscovery: api.confirmProjectApiDiscovery,
    activateProjectApiDiscovery: api.activateProjectApiDiscovery,
    saveProjectApiDiscoveryCredential: api.saveProjectApiDiscoveryCredential,
  };
}

function publicApiFor(api: WorkspacePageApi): ProjectAccessSettingsApi | null {
  if (!api.getProjectPublicApiSettings || !api.updateProjectPublicApiSettings) return null;
  return {
    getProjectPublicApiSettings: api.getProjectPublicApiSettings,
    updateProjectPublicApiSettings: api.updateProjectPublicApiSettings,
  };
}

function sourceSyncApiFor(api: WorkspacePageApi): ProjectRevisionHistoryApi | null {
  if (!api.getProjectRevisionHistory || !api.selectProjectSourceCandidate || !api.publishProjectSourceCandidate) return null;
  return {
    getProjectRevisionHistory: api.getProjectRevisionHistory,
    selectProjectSourceCandidate: api.selectProjectSourceCandidate,
    publishProjectSourceCandidate: api.publishProjectSourceCandidate,
  };
}

function CapabilityBoundary({ decision, label, transportAvailable = true, children }: {
  decision: WorkspaceCapabilityDecision;
  label: string;
  transportAvailable?: boolean;
  children?: ReactNode;
}) {
  if (decision.state === "enabled" && transportAvailable) return <>{children}</>;
  const state = decision.state === "enabled" ? "temporarily_unavailable" : decision.state;
  return <p className="workspace-tool-lock">{capabilityMessage(label, state)}</p>;
}

function capabilityMessage(label: string, state: Exclude<WorkspaceCapabilityDecision["state"], "enabled">): string {
  const singular = label === "Managed AI" || label === "Source sync";
  switch (state) {
    case "upgrade_required":
      return `${label} ${singular ? "requires" : "require"} a workspace plan upgrade.`;
    case "permission_denied":
      return `Your workspace role cannot manage ${label}.`;
    case "access_paused":
      return `${label} ${singular ? "is" : "are"} paused until workspace access is restored.`;
    case "temporarily_unavailable":
      return `${label} ${singular ? "is" : "are"} temporarily unavailable.`;
  }
}

function teamSkillsApiFor(api: WorkspacePageApi): TeamSkillsLibraryApi | null {
  if (!api.getTeamSkillsLibrary || !api.getTeamSkillHistory || !api.getTeamSkillVersionComparison || !api.publishTeamSkillVersion
    || !api.changeTeamSkillState || !api.beginTeamSkillOwnerStepUp || !api.completeTeamSkillOwnerAction
    || !api.replaceTeamSkillTags || !api.uploadTeamSkill) return null;
  return {
    getTeamSkillsLibrary: api.getTeamSkillsLibrary,
    getTeamSkillHistory: api.getTeamSkillHistory,
    getTeamSkillVersionComparison: api.getTeamSkillVersionComparison,
    publishTeamSkillVersion: api.publishTeamSkillVersion,
    changeTeamSkillState: api.changeTeamSkillState,
    beginTeamSkillOwnerStepUp: api.beginTeamSkillOwnerStepUp,
    completeTeamSkillOwnerAction: api.completeTeamSkillOwnerAction,
    replaceTeamSkillTags: api.replaceTeamSkillTags,
    uploadTeamSkill: api.uploadTeamSkill,
  };
}

function ProjectDeleteControl({ workspaceId, project, api, onDeleted }: {
  workspaceId: string;
  project: WorkspaceOverview["projects"][number];
  api: Pick<WorkspacePageApi, "deleteWorkspaceProject">;
  onDeleted(projectId: string, removedDomains: number): void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [confirmation, setConfirmation] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const remove = async () => {
    if (confirmation !== project.name || deleting) return;
    setDeleting(true);
    setError(null);
    try {
      const result = await api.deleteWorkspaceProject(workspaceId, project.id, confirmation);
      onDeleted(project.id, result.removedDomains);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Doorway could not delete this app.");
      setDeleting(false);
    }
  };

  return (
    <section className="workspace-delete-zone" aria-label={`Delete ${project.name}`}>
      <div>
        <p className="step-label">Danger zone</p>
        <h3>Delete this app</h3>
        <p>This removes its link, revisions, files, saved data, AI connection, sessions, and custom domains.</p>
      </div>
      {!confirming ? (
        <button className="danger-action-button" type="button" onClick={() => setConfirming(true)}>Delete app</button>
      ) : (
        <div className="workspace-delete-confirmation">
          <label>
            <span>Type <strong>{project.name}</strong> to confirm</span>
            <input value={confirmation} autoComplete="off" onChange={(event) => setConfirmation(event.target.value)} />
          </label>
          {error && <p className="error-message" role="alert">{error}</p>}
          <div>
            <button className="danger-action-button" type="button" disabled={confirmation !== project.name || deleting} onClick={() => void remove()}>{deleting ? "Deleting..." : "Delete permanently"}</button>
            <button className="text-button" type="button" disabled={deleting} onClick={() => { setConfirming(false); setConfirmation(""); setError(null); }}>Cancel</button>
          </div>
        </div>
      )}
    </section>
  );
}

function WorkspaceSettingsPanel({ workspace, api, onUpdated }: {
  workspace: WorkspaceOverview["workspace"];
  api: Pick<WorkspacePageApi, "updateWorkspaceSettings">;
  onUpdated(workspace: WorkspaceOverview["workspace"]): void;
}) {
  const [name, setName] = useState(workspace.name);
  const [pendingIcon, setPendingIcon] = useState<string | null | undefined>(undefined);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const previewIcon = pendingIcon === undefined ? workspace.iconUrl : pendingIcon ?? undefined;

  const chooseIcon = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    setNotice(null);
    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type) || file.size > 262_144) {
      setError("Choose a PNG, JPEG, or WebP image no larger than 256 KB.");
      return;
    }
    try {
      setPendingIcon(await readFileAsDataUrl(file));
    } catch {
      setError("Doorway could not read that image. Choose another file.");
    }
  };

  const save = async () => {
    const trimmedName = name.trim().replace(/\s+/g, " ");
    if (trimmedName.length < 1 || trimmedName.length > 120 || saving) return;
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const result = await api.updateWorkspaceSettings(workspace.id, {
        name: trimmedName,
        ...(pendingIcon !== undefined ? { iconDataUrl: pendingIcon } : {}),
      });
      setName(result.workspace.name);
      setPendingIcon(undefined);
      onUpdated(result.workspace);
      setNotice("Workspace settings saved.");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Doorway could not save these workspace settings.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="workspace-section workspace-settings-section" aria-labelledby="settings-heading">
      <div className="workspace-section-heading"><div><p className="step-label">Settings</p><h2 id="settings-heading">Workspace identity</h2></div></div>
      <p className="workspace-section-copy">Give this shared space a name and an icon your team will recognize.</p>
      {workspace.role !== "owner" ? (
        <p className="workspace-empty">Only the workspace owner can change its name or icon.</p>
      ) : (
        <div className="workspace-identity-editor">
          <div className="workspace-icon-editor">
            <span className="workspace-icon-preview" aria-hidden="true">
              {previewIcon ? <img src={previewIcon} alt="" /> : <span className="workspace-nav-door"><span /></span>}
            </span>
            <div>
              <strong>Workspace icon</strong>
              <p>PNG, JPEG, or WebP. Square images work best. Maximum 256 KB.</p>
              <label className="secondary-button workspace-icon-upload">Choose image<input type="file" accept="image/png,image/jpeg,image/webp" onChange={(event) => { void chooseIcon(event.target.files?.[0]); event.currentTarget.value = ""; }} /></label>
              {previewIcon && <button className="text-button danger-button" type="button" onClick={() => setPendingIcon(null)}>Remove icon</button>}
            </div>
          </div>
          <label className="workspace-name-field"><span>Workspace name</span><input value={name} maxLength={120} onChange={(event) => setName(event.target.value)} /></label>
          {error && <p className="error-message" role="alert">{error}</p>}
          {notice && <p className="workspace-notice" role="status">{notice}</p>}
          <button className="primary-button workspace-settings-save" type="button" disabled={saving || name.trim().length < 1} onClick={() => void save()}>{saving ? "Saving..." : "Save changes"}</button>
        </div>
      )}
    </section>
  );
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error("File reading failed."));
    reader.onload = () => typeof reader.result === "string" ? resolve(reader.result) : reject(new Error("File reading failed."));
    reader.readAsDataURL(file);
  });
}

function Limit({ label, value }: { label: string; value: string | number }) {
  return <div><strong>{value}</strong><span>{label}</span></div>;
}

const AUDIT_ACTION_LABELS = {
  [AUDIT_ACTIONS.workspaceBootstrap]: "Created the workspace",
  [AUDIT_ACTIONS.workspaceUpdated]: "Updated workspace details",
  [AUDIT_ACTIONS.membershipCreated]: "Added a workspace member",
  [AUDIT_ACTIONS.membershipRoleChanged]: "Changed a member's role",
  [AUDIT_ACTIONS.membershipRevoked]: "Removed a workspace member",
  [AUDIT_ACTIONS.projectVisibilityChanged]: "Changed site access",
  [AUDIT_ACTIONS.projectPublished]: "Published a site",
  [AUDIT_ACTIONS.projectDeleted]: "Deleted a site",
  [AUDIT_ACTIONS.secretChanged]: "Updated an AI provider key",
  [AUDIT_ACTIONS.customDomainChanged]: "Updated a custom domain",
  [AUDIT_ACTIONS.appDataRestored]: "Restored saved app data",
  [AUDIT_ACTIONS.entitlementChanged]: "Updated plan access",
  [AUDIT_ACTIONS.billingStateChanged]: "Updated billing status",
  [AUDIT_ACTIONS.billingPlanIntentRecorded]: "Started a plan change",
  [AUDIT_ACTIONS.teamDraftSaved]: "Saved Team setup",
  [AUDIT_ACTIONS.publicApiPolicyChanged]: "Changed public API policy",
} satisfies Record<AuditAction, string>;

function auditActionLabel(action: string): string {
  const known = AUDIT_ACTION_LABELS[action as AuditAction];
  if (known) return known;
  const readable = action.replace(/[._-]+/g, " ").trim();
  return readable ? `${readable.charAt(0).toUpperCase()}${readable.slice(1)}` : "Updated the workspace";
}

const UPGRADE_FEATURES = {
  solo: [
    "5 live apps",
    "25,000 visits each month",
    "1 custom domain",
    "Remove Doorway branding",
    "30 days of revision history",
  ],
  team: [
    "25 live apps and 100,000 monthly visits",
    "10 creators and unlimited approved viewers",
    "10 custom domains",
    "Private apps with Google sign-in",
    "Roles, offboarding, and audit history",
  ],
} as const;

function UpgradeCard({
  plan,
  cadence,
  quantity = 1,
  onQuantityChange,
  pending,
  disabled,
  onUpgrade,
}: {
  plan: "solo" | "team";
  cadence: BillingCadence;
  quantity?: number;
  onQuantityChange?: (quantity: number) => void;
  pending: boolean;
  disabled: boolean;
  onUpgrade: () => void;
}) {
  const definition = LAUNCH_PLAN_CATALOG.plans[plan];
  const cents = cadence === "monthly" ? definition.prices.monthlyCents : definition.prices.annualCents;
  return (
    <article className={`upgrade-card upgrade-${plan}`}>
      <div className="upgrade-card-heading">
        <div>
          <span>{plan === "solo" ? "For one person" : "For a shared workspace"}</span>
          <h3>{definition.name}</h3>
        </div>
        {plan === "team" && <span className="upgrade-team-badge">Everything teams need</span>}
      </div>
      <div className="upgrade-price">
        <strong>{formatPrice(cents * quantity)}</strong>
        <span>per {cadence === "monthly" ? "month" : "year"}{plan === "solo" ? ` for ${quantity} branded ${quantity === 1 ? "site" : "sites"}` : ""}</span>
      </div>
      {cadence === "annual" && <p className="upgrade-savings">Two months free with annual billing</p>}
      <ul>{UPGRADE_FEATURES[plan].map((feature) => <li key={feature}>{feature}</li>)}</ul>
      {plan === "solo" && onQuantityChange && (
        <label className="solo-site-quantity">
          <span>Custom-domain sites</span>
          <input
            type="number"
            min={1}
            max={MAX_SOLO_BRANDED_SITES}
            value={quantity}
            disabled={disabled}
            onChange={(event) => onQuantityChange(Math.min(MAX_SOLO_BRANDED_SITES, Math.max(1, Number(event.target.value) || 1)))}
          />
        </label>
      )}
      <button className={plan === "team" ? "primary-button" : "secondary-button"} type="button" disabled={disabled} onClick={onUpgrade}>
        {pending ? "Opening checkout..." : `Upgrade to ${definition.name}`}
      </button>
    </article>
  );
}

function formatPrice(cents: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
    maximumFractionDigits: cents % 100 === 0 ? 0 : 2,
  }).format(cents / 100);
}

function currentPlanDescription(plan: "free" | "solo" | "team"): string {
  if (plan === "free") return "Keep one app live for free. Upgrade when you need more apps, more traffic, a custom domain, or teammates.";
  if (plan === "solo") return "More room for the apps you rely on, with your own domain and removable Doorway branding.";
  return "A shared home for internal apps, with private access, roles, custom domains, and room for your team.";
}

function billingLabel(overview: WorkspaceOverview): string {
  if (overview.plan.effective === "free") return "No card required";
  if (overview.plan.billingCadence === "annual") return "Billed annually";
  if (overview.plan.billingCadence === "monthly") return "Billed monthly";
  return "Billing active";
}

function visitLimit(overview: WorkspaceOverview): { label: string; value: string } {
  const visits = overview.usage.find((meter) => meter.meter === "app_visits");
  if (!visits?.configured) {
    return { label: "Monthly visits included", value: overview.limits.monthlyVisits.toLocaleString() };
  }
  return {
    label: "Visits this month",
    value: `${visits.used.toLocaleString()} / ${visits.limit.toLocaleString()}`,
  };
}

function planTitle(plan: "free" | "solo" | "team"): string {
  return LAUNCH_PLAN_CATALOG.plans[plan].name;
}

function planNotice(result: PlanIntentResult): string {
  const plan = planTitle(result.desiredPlan);
  return result.billingReady
    ? `Opening secure checkout for ${plan}.`
    : `${plan} checkout is not available yet. Your current plan has not changed.`;
}

function initialBillingCadence(): BillingCadence {
  if (typeof window === "undefined") return "monthly";
  return new URLSearchParams(window.location.search).get("cadence") === "annual" ? "annual" : "monthly";
}

function initialWorkspaceView(): WorkspaceView {
  if (typeof window === "undefined") return "sites";
  if (new URLSearchParams(window.location.search).has("plan")) return "billing";
  const hash = window.location.hash.slice(1);
  return WORKSPACE_VIEWS.some((view) => view.id === hash) ? hash as WorkspaceView : "sites";
}

function workspaceViewBadge(view: WorkspaceView, overview: WorkspaceOverview): string | number {
  if (view === "sites") return overview.projects.length;
  if (view === "skills") return "Team";
  if (view === "ai") return "Private";
  if (view === "domains") return `${overview.domainUsage.used}/${overview.domainUsage.limit}`;
  if (view === "people") return overview.members.length;
  if (view === "billing") return planTitle(overview.plan.effective);
  if (view === "history") return overview.audit.length;
  return overview.workspace.role === "owner" ? "Owner" : "View";
}

function filterProjects(projects: WorkspaceOverview["projects"], search: string): WorkspaceOverview["projects"] {
  const query = search.trim().toLocaleLowerCase();
  if (!query) return projects;
  return projects.filter((project) => [
    project.name,
    project.customDomain,
    project.description,
    project.ownerName,
    project.status,
    project.visibility,
  ].some((value) => value?.toLocaleLowerCase().includes(query)));
}

function projectTitle(project: WorkspaceOverview["projects"][number]): string {
  return project.customDomain ?? project.name;
}

function withCustomDomain(
  project: WorkspaceOverview["projects"][number],
  customDomain: string | null,
): WorkspaceOverview["projects"][number] {
  const { customDomain: _currentDomain, ...base } = project;
  return customDomain ? { ...base, customDomain } : base;
}

function visibilityLabel(visibility: WorkspaceOverview["projects"][number]["visibility"]): string {
  if (visibility === "workspace") return "Workspace only";
  if (visibility === "password") return "Password";
  return "Public";
}

function formatDate(value: string): string {
  return new Intl.DateTimeFormat("en", { month: "short", day: "numeric", year: "numeric" }).format(new Date(value));
}
